import React, { useState } from 'react';
import { Modal, Form, Select, Upload, Button, Progress, Table, Tag, Space, App } from 'antd';
import { InboxOutlined, DownloadOutlined } from '@ant-design/icons';
import { importOrganization } from '@/services/ant-design-pro/api';
import type { API } from '@/types/api';
import { downloadFile, buildDownloadUrl } from '@/utils/download';

const { Dragger } = Upload;
const { Option } = Select;

interface OrganizationImportModalProps {
  visible: boolean;
  onCancel: () => void;
  onSuccess: () => void;
}

const OrganizationImportModal: React.FC<OrganizationImportModalProps> = ({
  visible,
  onCancel,
  onSuccess,
}) => {
  const { message } = App.useApp();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [fileList, setFileList] = useState<any[]>([]);
  const [importResult, setImportResult] = useState<API.OrganizationImportResult | null>(null);
  const [step, setStep] = useState<'upload' | 'result'>('upload');
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
  });

  // 重置状态
  const resetState = () => {
    setFileList([]);
    setImportResult(null);
    setStep('upload');
    setPagination({
      current: 1,
      pageSize: 10,
    });
    form.resetFields();
  };

  // 处理取消
  const handleCancel = () => {
    resetState();
    onCancel();
  };

  // 处理文件上传
  const handleUpload = {
    beforeUpload: (file: File) => {
      const isExcel = file.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
                     file.type === 'application/vnd.ms-excel';
      if (!isExcel) {
        message.error('只能上传 Excel 文件!');
        return false;
      }
      const isLt10M = file.size / 1024 / 1024 < 10;
      if (!isLt10M) {
        message.error('文件大小不能超过 10MB!');
        return false;
      }
      return false; // 阻止自动上传
    },
    onChange: (info: any) => {
      setFileList(info.fileList.slice(-1)); // 只保留最后一个文件
    },
  };


  // 处理导入
  const handleImport = async () => {
    if (fileList.length === 0) {
      message.error('请先选择要导入的文件');
      return;
    }

    const values = await form.validateFields();
    setLoading(true);

    try {
      const response = await importOrganization(values.orgType, fileList[0].originFileObj);

      if (response.success && response.result) {
        setImportResult(response.result);
        setStep('result');
        // 重置分页到第一页
        setPagination({
          current: 1,
          pageSize: pagination.pageSize,
        });
        message.success('导入完成');
      } else {
        message.error(response.message || '导入失败');
      }
    } catch (error) {
      console.error('导入失败:', error);
      message.error('导入失败，请稍后重试');
    } finally {
      setLoading(false);
    }
  };

  // 下载失败文件
  const handleDownloadFailedFile = () => {
    if (!importResult?.storageVO?.id) {
      message.error('失败文件不存在');
      return;
    }

    try {
      // 构建下载URL - 使用导入接口返回的ID
      const downloadUrl = buildDownloadUrl(importResult.storageVO.id);

      // 使用通用下载方法
      downloadFile(downloadUrl, '机构导入失败文件.xlsx');

      message.success('失败文件下载成功');
    } catch (error) {
      console.error('下载失败文件失败:', error);
      message.error('下载失败文件失败，请稍后重试');
    }
  };

  // 结果表格列定义
  const resultColumns = [
    {
      title: '机构名称',
      dataIndex: 'organizationName',
      key: 'organizationName',
      width: 150,
    },
    {
      title: '父机构名称',
      dataIndex: 'parentOrganizationName',
      key: 'parentOrganizationName',
      width: 150,
    },
    {
      title: '手机号码',
      dataIndex: 'mobilePhone',
      key: 'mobilePhone',
      width: 120,
    },
    {
      title: '传真',
      dataIndex: 'fax',
      key: 'fax',
      width: 120,
    },
    {
      title: '详细地址',
      dataIndex: 'detailedAddress',
      key: 'detailedAddress',
      width: 200,
    },
    {
      title: '状态',
      dataIndex: 'succeed',
      key: 'succeed',
      width: 100,
      render: (succeed: string) => (
        <Tag color={succeed === '成功' ? 'green' : 'red'}>
          {succeed === '成功' ? '成功' : '失败'}
        </Tag>
      ),
    },
    {
      title: '失败原因',
      dataIndex: 'causeFailure',
      key: 'causeFailure',
      width: 200,
      render: (causeFailure: string) => causeFailure || '-',
    },
  ];

  return (
    <Modal
      title="导入机构"
      open={visible}
      onCancel={handleCancel}
      width={800}
      maskClosable={false}
      footer={
        step === 'upload' ? (
          <Space>
            <Button onClick={handleCancel}>取消</Button>
            <Button
              type="primary"
              loading={loading}
              onClick={handleImport}
              disabled={fileList.length === 0}
            >
              开始导入
            </Button>
          </Space>
        ) : (
          <Space>
            <Button onClick={handleCancel}>关闭</Button>
            {importResult?.storageVO && (
              <Button
                icon={<DownloadOutlined />}
                onClick={handleDownloadFailedFile}
              >
                下载失败文件
              </Button>
            )}
            <Button type="primary" onClick={() => {
              resetState();
              onSuccess();
            }}>
              完成
            </Button>
          </Space>
        )
      }
    >
      {step === 'upload' ? (
        <div>
          <Form form={form} layout="vertical">
            <Form.Item
              name="orgType"
              label="机构类型"
              rules={[{ required: true, message: '请选择机构类型' }]}
              initialValue="SUB"
            >
              <Select placeholder="请选择机构类型">
                {/* <Option value="MAIN">主机构</Option> */}
                <Option value="SUB">子机构</Option>
                {/* <Option value="COLLABORATIVE">协作机构</Option> */}
              </Select>
            </Form.Item>

            <Form.Item label="选择文件">
              <Dragger
                {...handleUpload}
                fileList={fileList}
                accept=".xlsx,.xls"
                maxCount={1}
              >
                <p className="ant-upload-drag-icon">
                  <InboxOutlined />
                </p>
                <p className="ant-upload-text">点击或拖拽文件到此区域上传</p>
                <p className="ant-upload-hint">
                  支持单个文件上传，仅支持 Excel 格式文件
                </p>
              </Dragger>
            </Form.Item>
          </Form>


          {loading && (
            <div style={{ marginTop: 16 }}>
              <Progress percent={50} status="active" />
              <p style={{ textAlign: 'center', marginTop: 8 }}>
                正在导入机构信息，请稍候...
              </p>
            </div>
          )}
        </div>
      ) : (
        <div>
          <div style={{ marginBottom: 16 }}>
            <h3>导入结果</h3>
            <div style={{ display: 'flex', gap: 16, marginBottom: 16 }}>
              <Tag color="green">成功: {importResult?.numberSuccesses || 0}</Tag>
              <Tag color="red">失败: {importResult?.numberFail || 0}</Tag>
            </div>
          </div>

          <Table
            columns={resultColumns}
            dataSource={importResult?.succeedList || []}
            rowKey={(record, index) => `${record.organizationName}-${index}`}
            pagination={{
              current: pagination.current,
              pageSize: pagination.pageSize,
              showSizeChanger: true,
              showQuickJumper: true,
              showTotal: (total) => `共 ${total} 条记录`,
              pageSizeOptions: ['10', '20', '50', '100'],
              onChange: (page, pageSize) => {
                setPagination({
                  current: page,
                  pageSize: pageSize || pagination.pageSize,
                });
              },
              onShowSizeChange: (current, size) => {
                setPagination({
                  current: 1, // 切换每页条数时重置到第一页
                  pageSize: size,
                });
              },
            }}
            scroll={{ x: 1000 }}
            size="small"
          />
        </div>
      )}
    </Modal>
  );
};

export default OrganizationImportModal;
